import { Flex, Table, Text } from '@chakra-ui/react';
import { PredictionPosition } from '@features/positions/predictions/types';
import { memo } from 'react';
import { LuChevronRight } from 'react-icons/lu';
import { InfoIcon } from 'vectors';

interface PositionRowProps {
  position: PredictionPosition;
  index: number;
  totalCount: number;
  onClick: (position: PredictionPosition) => void;
}

const PositionRow = memo(({ position, index, totalCount, onClick }: PositionRowProps) => (
  <Table.Row fontSize="sm" cursor="pointer" _hover={{ bg: 'bg.subtle' }} onClick={() => onClick(position)}>
    <Table.Cell
      border="none"
      p={4}
      borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
      borderBottomLeftRadius={index === totalCount - 1 ? 'xl' : 'none'}
    >
      <Flex direction="column" gap={0.5}>
        <Text fontWeight="medium">{position.market}</Text>
        <Text fontSize="sm" fontWeight="medium">You think: {position.outcome}</Text>
      </Flex>
    </Table.Cell>
    <Table.Cell border="none" p={4}>
      <Text>
        {position.avgPrice.toFixed(2)} → {position.currentPrice.toFixed(2)}
      </Text>
    </Table.Cell>
    <Table.Cell border="none" p={4} hideBelow="md">
      <Text>{position.traded}</Text>
    </Table.Cell>
    <Table.Cell border="none" p={4}>
      <Flex direction="column" gap={0.5}>
        <Text fontWeight="medium">${position.toWin.toFixed(2)}</Text>
        <Text fontSize="xs" color="fg.subtle">Current: ${position.value.toFixed(2)}</Text>
      </Flex>
    </Table.Cell>
    <Table.Cell
      border="none"
      p={4}
      pr={2}
      borderTopRightRadius={index === 0 ? 'xl' : 'none'}
      borderBottomRightRadius={index === totalCount - 1 ? 'xl' : 'none'}
      w="1"
    >
      <LuChevronRight size={16} color="var(--chakra-colors-text-secondary)" />
    </Table.Cell>
  </Table.Row>
));
PositionRow.displayName = 'PositionRow';

interface Props {
  positions: PredictionPosition[];
  onRowClick: (position: PredictionPosition) => void;
}

const PositionsTable = memo(({ positions, onRowClick }: Props) => {
  return (
    <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2} overflowX="auto">
      <Table.Root>
        <Table.Header fontSize="sm" fontWeight="medium">
          <Table.Row bg="transparent">
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>MARKET</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Flex direction="row" gap={1} alignItems="center">
                <Text>BUY PRICE → CURRENT</Text>
                <InfoIcon boxSize={3} color="text.secondary" />
              </Flex>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4} hideBelow="md">
              <Text>TRADED</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>IF CORRECT</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" p={4} w="1" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {positions.map((position, index) => (
            <PositionRow
              key={position.id}
              position={position}
              index={index}
              totalCount={positions.length}
              onClick={onRowClick}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
});
PositionsTable.displayName = 'PositionsTable';

export default PositionsTable;
